import React from "react"
import { useTranslation } from "react-i18next"
import FormElement from "components/Form/FormElement"
import { LocalEntity } from "models"
import { FormError } from "models/form"
import { BC_OPERATOR_NAME, MFC_OPERATOR_NAME } from "utils/constants"
import { useAuth } from "hooks"
import { booleanToString } from "utils/functions"
import { isNotEmpty } from "utils"

type BikeFLeetFormProps = {
	partner: LocalEntity
	setUpdatedPartner: React.Dispatch<React.SetStateAction<Partial<LocalEntity>>>
	setErrors: React.Dispatch<React.SetStateAction<Array<FormError>>>
	isEdit: boolean
	showLocalErrors: boolean
	isMFCSelected: boolean
	isBCSelected: boolean
}

export default function BikeFleetAccountIdForm({
	partner,
	setUpdatedPartner,
	setErrors,
	isMFCSelected,
	isBCSelected,
	isEdit,
	showLocalErrors,
}: BikeFLeetFormProps) {
	const { t } = useTranslation()
	const { isAdminBicycode, isAdminMFC, isAdminOCode } = useAuth()
	const [formFields, setFormFields] = React.useState(partner)

	React.useEffect(() => {
		if (!isEdit) setFormFields(partner)
	}, [partner, isEdit])

	React.useEffect(
		() => {
			setUpdatedPartner((prevFormFields) => ({
				...prevFormFields,
				bc_account_id: formFields.bc_account_id,
				mfc_account_id: formFields.mfc_account_id,
				mfc_is_fleet: formFields.mfc_is_fleet,
				bc_is_fleet: formFields.bc_is_fleet,
			}))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[formFields]
	)

	const handleInputChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, type: string, fieldName) => {
			const target = event.target
			let value: string | boolean | React.ChangeEvent<HTMLInputElement>
			let name: string

			if (!target && (type = "date")) {
				name = fieldName
				value = event
			} else {
				value = target.type === "checkbox" ? target.checked : target.value
				name = target.name
			}

			setFormFields((s) => ({
				...s,
				[name]: value,
			}))
		},
		[]
	)

	const handleValidation = React.useCallback((name: string, errs: FormError[]) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
	}

	return (
		<section data-cy="bikeFleetForm" className="py-16 px-4">
			<h2 className="c-h2 font-semibold mb-7 uppercase">
				{t("store.bikeFleetManagement", { ns: "partner" })}
			</h2>
			<div className="l-grid u-mg-negative-hz-m">
				{(isAdminBicycode || isAdminOCode) && isBCSelected && (
					<div className="lg:w-1/2 w-full p-5">
						<FormElement
							{...formElementCommonProps}
							value={formFields.bc_account_id}
							name="bc_account_id"
							type="text"
							label={`${t("bikeFleet.accountID", {
								ns: "bike",
							})} - ${BC_OPERATOR_NAME}`}
							className="mb-5"
							readonly={!isEdit || !isBCSelected}
						/>
					</div>
				)}

				{(isAdminMFC || isAdminOCode) && isMFCSelected && (
					<div className="lg:w-1/2 w-full p-5">
						<FormElement
							{...formElementCommonProps}
							value={formFields.mfc_account_id}
							name="mfc_account_id"
							type="text"
							label={`${t("bikeFleet.accountID", {
								ns: "bike",
							})} - ${MFC_OPERATOR_NAME}`}
							className="mb-5"
							readonly={!isEdit || !isMFCSelected}
						/>
					</div>
				)}
			</div>
			<div className="l-grid u-mg-negative-hz-m">
				{(isAdminBicycode || isAdminOCode) && isBCSelected && (
					<div className="lg:w-1/2 w-full p-5">
						<FormElement
							{...formElementCommonProps}
							value={booleanToString(formFields.bc_is_fleet)}
							name="bc_is_fleet"
							type="radio"
							label={t("store.isAuthorizedManagementFleetBiCyCode", {
								ns: "partner",
							})}
							options={[
								{
									id: "fleet-management-bicycode-true",
									value: "true",
									label: "global.yes",
								},
								{
									id: "fleet-management-bicycode-false",
									value: "false",
									label: "global.no",
								},
							]}
							className="mb-7"
							classNameOptionsContainer="flex-row w-full"
							classNameLabel="mb-5"
							classNameOption="flex-1 items-center"
							disabled={
								!isEdit || !isBCSelected || !isNotEmpty(formFields.bc_account_id)
							}
						/>
					</div>
				)}

				{(isAdminMFC || isAdminOCode) && isMFCSelected && (
					<div className="lg:w-1/2 w-full p-5">
						<FormElement
							{...formElementCommonProps}
							value={booleanToString(formFields.mfc_is_fleet)}
							name="mfc_is_fleet"
							type="radio"
							label={t("store.isAuthorizedManagementFleetMFC", { ns: "partner" })}
							options={[
								{
									id: "fleet-management-mfc-true",
									value: "true",
									label: "global.yes",
								},
								{
									id: "fleet-management-mfc-false",
									value: "false",
									label: "global.no",
								},
							]}
							className="mb-7"
							classNameOptionsContainer="flex-row w-full"
							classNameLabel="mb-5"
							classNameOption="flex-1 items-center"
							disabled={
								!isEdit || !isMFCSelected || !isNotEmpty(formFields.mfc_account_id)
							}
						/>
					</div>
				)}
			</div>
		</section>
	)
}
